import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getPosts } from "../../services/api";
import PostItem from "./PostItem";

const PostList = ({ countryFilter }) => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const limit = 5;
  const observer = useRef();
  const topRef = useRef(null);

  // 🔍 IntersectionObserver para scroll infinito
  const lastPostRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + limit);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // 🔄 Reset cuando cambia el filtro de país
  useEffect(() => {
    setPosts([]);
    setOffset(0);
    setHasMore(true);

    if (topRef.current) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [countryFilter]);

  // 📡 Fetch de posts
useEffect(() => {

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { limit, offset };

      params.country = countryFilter ?? "";


      const res = await getPosts(params);
      setPosts((prev) => {
        const newPosts = res.data.results.filter(
          (newPost) => newPost.id && !prev.some((p) => p.id === newPost.id)
        );
        return offset === 0 ? newPosts : [...prev, ...newPosts];
      });

      setHasMore(res.data.next !== null);
    } catch (err) {
      console.error("Error al cargar posts", err);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [offset, countryFilter]);


  const handlePostUpdated = (update) => {
    setPosts((prev) =>
      prev.map((p) => {
        if (typeof update === "function") {
          const result = update(p);
          return p.id === result.id ? result : p;
        }
        return p.id === update.id ? update : p;
      })
    );
  };

  const handlePostDeleted = (deletedId) => {
    setPosts((prev) => prev.filter((p) => p.id !== deletedId));
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);
  };

  return (
    <>
      <div
        className="max-w-3xl mx-auto px-0 sm:px-2 pt-3"
        aria-label={t("post.list.aria_label")}
        role="feed"
      >
        <div ref={topRef} />

        {posts.map((post, i) => (
          <div
            key={post.id}
            ref={i === posts.length - 1 ? lastPostRef : null}
            className="pb-5 sm:pb-6"
          >
            <PostItem
              post={post}
              onPostUpdate={handlePostUpdated}
              onPostDelete={handlePostDeleted}
              onImageClick={(url) => setModalImage(url)}
            />
          </div>
        ))}

        {loading && (
          <div
            className="text-center py-4 text-gray-500"
            role="status"
            aria-live="polite"
          >
            {t("search_results.loading", "Cargando más resultados...")}
          </div>
        )}


        {!loading && posts.length === 0 && (
          <p className="text-center text-sm text-gray-400 mt-6 italic">
            {t("post.list.empty")}
          </p>
        )}
      </div>

      {showSnackbar && (
        <div
          role="status"
          aria-live="assertive"
          className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg text-sm z-50 animate-fade-in whitespace-nowrap w-max max-w-[calc(100vw-1rem)]"
        >
          {t("post.list.deleted_success")}
        </div>
      )}

      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-label={t("post.list.image_modal_label")}
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative max-w-full max-h-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalImage(null)}
              className="absolute top-2 right-2 text-white bg-gray-800 bg-opacity-70 hover:bg-opacity-90 px-3 py-1 rounded-full text-sm"
              aria-label={t("post.list.close")}
            >
              {t("post.list.close")}
            </button>
            <img
              src={modalImage}
              alt={t("post.list.expanded_image_alt")}
              className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-xl"
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-in-out;
        }
      `}</style>
    </>
  );
};

export default PostList;
